import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import {
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  expand,
  lastValueFrom,
  Subject,
  switchMap,
  reduce,
} from "rxjs";
import buildQuery from "odata-query";
import { ImportUser } from "../../../../core/models/users/users/importUser";
import { Users } from "../../../../core/models/users/users/users";
import { UsersList } from "../../../../core/models/users/users/usersList";
import { CommonModule } from "@angular/common";
import { MsalApiService } from "../../../../core/services/msal-api.service";
import { ScrollerModule } from "primeng/scroller";
import { Table, TableModule } from "primeng/table";
import { InputGroupModule } from "primeng/inputgroup";
import { InputGroupAddonModule } from "primeng/inputgroupaddon";
import { FloatLabelModule } from "primeng/floatlabel";
import { TooltipModule } from "primeng/tooltip";
import {
  ADGroup,
  ADGroupResponse,
} from "../../../../core/models/ad-group.model";
import { ADUser, ADUsersResponse } from "../../../../core/models/ad-user.model";
import { Drawer } from "primeng/drawer";
import { ButtonModule } from "primeng/button";
import { JniToastrService } from "../../../../core/services/jni-toastr.service";
import { ImportUserService } from "../../../../core/services/import-user.service";
@UntilDestroy()
@Component({
  selector: "jni-import-user",
  templateUrl: "./import-user.component.html",
  imports: [
    CommonModule,
    TableModule,
    ScrollerModule,
    InputGroupModule,
    InputGroupAddonModule,
    FloatLabelModule,
    TooltipModule,
    ButtonModule,
  ],
  styleUrls: ["./import-user.component.scss"],
})
export class ImportUserComponent implements OnInit, OnChanges {
  @ViewChild("dt") table!: Table;
  @ViewChild("drawerUserImportRef") drawerRef!: Drawer;
  importUsers: ADUser[] = [];
  unFilteredImportUsers: ADUser[] = [];
  public loading = false;
  public showLoader = false;
  public selectedUsers: Users[] = [];
  private globalSearch!: string;
  public loadingComplete = false;
  public search$ = new Subject<string>();

  @Input() userListings!: UsersList[];
  @Output() userListChanged: EventEmitter<boolean> =
    new EventEmitter<boolean>();
  @Output() toggleImportDrawer: EventEmitter<boolean> =
    new EventEmitter<boolean>();
  constructor(
    private _importUserService: ImportUserService,
    private _msalApisService: MsalApiService,
    private _toastr: JniToastrService
  ) {}

  ngOnInit() {
    this.fetchADUsers();
    this.searchFilter();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes["userListings"] &&
      changes["userListings"].firstChange == false
    ) {
      this.showLoader = false;
      this.globalSearch = "";
      this.loadingComplete = false;
      this.unFilteredImportUsers = [];
      this.fetchADUsers();
    }
  }
  searchFilter() {
    this.search$
      .pipe(
        debounceTime(1000),
        distinctUntilChanged(),
        switchMap(searchItem => {
          if (searchItem == "") {
            this.globalSearch = "";
            setTimeout(() => {
              this.importUsers = this.unFilteredImportUsers;
            }, 1000);
            return EMPTY;
          }
          if (this.loadingComplete) {
            this.importUsers = this.unFilteredImportUsers.filter(x => {
              return (
                this.getLoginEmail(
                  x.userPrincipalName ?? "",
                  x.userPrincipalName ?? ""
                )
                  ?.toLowerCase()
                  .includes(searchItem.toLowerCase()) ||
                x.displayName?.toLowerCase().includes(searchItem.toLowerCase())
              );
            });
            this.globalSearch = searchItem;
            return EMPTY;
          }
          this.globalSearch = searchItem;
          const filter = {
            or: [
              {
                displayName: {
                  startswith: searchItem,
                },
              },
              {
                userPrincipalName: {
                  startswith: searchItem,
                },
              },
            ],
          };
          const query = buildQuery({ filter });
          return this._msalApisService.getFilteredUsers(query);
        })
      )
      .subscribe((res: ADUsersResponse) => {
        if (!this.loadingComplete) {
          this.importUsers = res.value.filter(
            ({
              userPrincipalName,
              mail,
            }: {
              userPrincipalName: string;
              mail: string;
            }) =>
              !this.userListings.some(
                ({ loginEmail }) =>
                  loginEmail === this.getLoginEmail(userPrincipalName, mail) ||
                  !this.getLoginEmail(userPrincipalName, mail)
              )
          );
        } else {
          this.importUsers = this.unFilteredImportUsers.filter(x => {
            return (
              this.getLoginEmail(
                x.userPrincipalName ?? "",
                x.userPrincipalName ?? ""
              )
                ?.toLowerCase()
                .includes(this.globalSearch.toLowerCase()) ||
              x.displayName
                ?.toLowerCase()
                .includes(this.globalSearch.toLowerCase())
            );
          });
        }
      });
  }

  fetchADUsers(): void {
    this.loading = true;
    this._msalApisService
      .getADUsers()
      .pipe(
        expand((val: ADUsersResponse) => {
          if (val["@odata.nextLink"]) {
            return this._msalApisService.getDataUsingODataLink(
              val["@odata.nextLink"]
            );
          } else {
            return EMPTY;
          }
        }),
        reduce((acc: ADUser[], x: ADUsersResponse) => {
          if (!acc.length) {
            this.importUsers = x.value;
            this.loading = false;
          }
          return acc.concat(x.value);
        }, [])
      )
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this.loadingComplete = true;
          const temp = res.filter(
            ({
              userPrincipalName,
              mail,
            }: {
              userPrincipalName: string;
              mail: string;
            }) =>
              !this.userListings.some(
                ({ loginEmail }) =>
                  loginEmail === this.getLoginEmail(userPrincipalName, mail) ||
                  !this.getLoginEmail(userPrincipalName, mail)
              )
          );
          this.unFilteredImportUsers = temp;

          if (this.globalSearch && this.globalSearch.length > 0) {
            this.importUsers = this.unFilteredImportUsers.filter(x => {
              return (
                this.getLoginEmail(x.userPrincipalName ?? "", x.mail ?? "")
                  ?.toLowerCase()
                  .includes(this.globalSearch.toLowerCase()) ||
                x.displayName
                  ?.toLowerCase()
                  .includes(this.globalSearch.toLowerCase())
              );
            });
          } else {
            this.importUsers = temp;
          }
          this.loading = false;
        },
        error: () => {
          this.showLoader = false;
        },
      });
  }

  public getLoginEmail(userPrincipalName: string, mail: string) {
    let loginEmail = "";
    if (userPrincipalName?.includes("#EXT#")) {
      loginEmail = mail;
    } else {
      loginEmail = userPrincipalName;
    }
    if (loginEmail?.includes("visionet.com")) {
      loginEmail = loginEmail.replace("visionet.com", "visionetsystems.com");
    }
    return loginEmail;
  }

  public createUser(users: ImportUser[]): void {
    this._importUserService
      .createUser(users)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this.toggleImportDrawer.emit(true);
          this.userListChanged.emit(true);
          this._toastr.success("Success", "User(s) added successfully!");
        },
        error: () => {
          this.showLoader = false;
        },
      });
  }

  public async importUsersHandler(): Promise<void> {
    this.showLoader = true;
    const payload: ImportUser[] = [];
    for (const user of this.selectedUsers) {
      const obj: ImportUser = {
        loginEmail: this.getLoginEmail(
          user.userPrincipalName ?? "",
          user.mail ?? ""
        ),
        name: user.displayName ?? "",
        group: "",
        adEmail: user.mail ?? "",
        adReferenceId: user.id ?? "",
      };
      const groups: ADGroupResponse = await lastValueFrom(
        this._msalApisService.getADUserGroups(user.id)
      );
      if (groups.value.length) {
        const group = groups.value.map((value: ADGroup) => value.mailNickname);
        obj.group = group.toString();
      } else {
        obj.group = "";
      }
      payload.push(obj);
    }
    if (payload.length > 0) {
      this.createUser(payload);
    }
  }

  closeImport() {
    this.toggleImportDrawer.emit(true);
  }

  getAndSyncUserMails() {
    this._msalApisService.getAndSyncUserMails().subscribe({
      next: res => {
        // eslint-disable-next-line no-console
        console.log(res);
      },
    });
  }
}
